import { useCallback, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { type SearchSuggestionsResult, type SearchType } from 'invictus-sdk-typescript'

import { type FavoritePlaces } from '@Components/container/search/searchCriteria/autocompleteFavoritePlaces/AutocompleteFavoritePlaces'
import { type AutocompleteResultModel } from '@DS/components/navigation/search/atoms/AutocompleteResult'
import { type AutocompleteSection } from '@DS/components/navigation/search/autocomplete/Autocomplete.types'

import { useSearchAutocompleteSuggestionsQuery } from './useSearchAutocompleteQuery'
import { mapFavoritePlacesToAutocompleteFavoritePlaces } from './useSearchAutocompleteSuggestions.utils'

type SearchAutocompleteSuggestions = {
  suggestions: AutocompleteSection<AutocompleteResultModel>[]
  favoritePlaces: FavoritePlaces
  hasReachedMaximumPlaces: boolean
}

const defaultEmptyObject = {}

export const useSearchAutocompleteSuggestions: (
  searchType: SearchType,
  options?: {
    shouldIgnoreSuggestions?: boolean
    shouldIgnoreFavoritePlaces?: boolean
  },
) => SearchAutocompleteSuggestions = (
  searchType,
  { shouldIgnoreSuggestions = false, shouldIgnoreFavoritePlaces = false } = defaultEmptyObject,
) => {
  const [{ suggestions, favoritePlaces, hasReachedMaximumPlaces }, setSuggestions] =
    useState<SearchAutocompleteSuggestions>({
      suggestions: [],
      favoritePlaces: { otherFavoritePlaces: [] },
      hasReachedMaximumPlaces: false,
    })

  const { formatMessage } = useIntl()

  const mapSearchSuggestionsResultToAutocompleteSections = useCallback(
    ({ recentSearches }: SearchSuggestionsResult): AutocompleteSection<AutocompleteResultModel>[] => [
      { title: formatMessage({ id: 'recentSearches' }), items: recentSearches },
    ],
    [formatMessage],
  )

  const isSet = useMemo(() => suggestions.length > 0, [suggestions.length])

  const {
    isSuccess,
    data: remoteSuggestions,
    error,
  } = useSearchAutocompleteSuggestionsQuery(searchType, !isSet && !shouldIgnoreSuggestions)

  useEffect(() => {
    if (isSuccess) {
      setSuggestions((old) => ({
        suggestions: mapSearchSuggestionsResultToAutocompleteSections(remoteSuggestions),
        favoritePlaces: shouldIgnoreFavoritePlaces
          ? old.favoritePlaces
          : mapFavoritePlacesToAutocompleteFavoritePlaces(remoteSuggestions.favoritePlaces),
        hasReachedMaximumPlaces: remoteSuggestions.hasReachedMaximumPlaces,
      }))
    }
  }, [
    remoteSuggestions,
    isSuccess,
    error,
    mapSearchSuggestionsResultToAutocompleteSections,
    shouldIgnoreFavoritePlaces,
  ])

  return {
    suggestions,
    favoritePlaces,
    hasReachedMaximumPlaces,
  }
}
